/*
 * @Author: Borden.Lan
 * @Date: 2022-06-16 09:36:06
 * @Description: 邮件授权
 */
import React, { useEffect, useCallback, useMemo } from 'react';
import { throttle, noop } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Trans } from '@tools/i18n';
import { Box } from '@kufox/mui';
import { useTheme, useSnackbar } from '@kufox/mui/hooks';
import { isPropValid, styled } from '@kufox/mui/emotion';
import { NAMESPACE } from '../constants';
import { useLang } from '../../hookTool';
import { MAIL_AUTHORIZE_EXPIRE_CODE } from '../../common/constants';
import { getTrackingSource, kcsensorsManualTrack } from '../../common/tools';

import emailAuthorizeSvg from '../../../static/email-authorize.svg';

const Title = styled('h2', {
  shouldForwardProp: (props) => isPropValid(props),
})(() => {
  return {
    marginTop: 24,
    fontSize: '24px',
    lineHeight: '36px',
  };
});

const Guide = styled(Box, {
  shouldForwardProp: (props) => isPropValid(props),
})(({ theme }) => {
  return {
    marginTop: 24,
    lineHeight: '26px',
    color: theme.colors.text,
    fontSize: '16px',
    '& .highlight': {
      color: theme.colors.primary,
    },
  };
});

const Tip = styled(Box, {
  shouldForwardProp: (props) => isPropValid(props),
})(({ theme }) => {
  return {
    marginTop: 24,
    lineHeight: '26px',
    color: theme.colors.text60,
    fontSize: '16px',
  };
});

const NoReceiveMail = styled(Box, {
  shouldForwardProp: (props) => isPropValid(props),
})(({ theme }) => {
  return {
    marginTop: 36,
    lineHeight: '26px',
    color: theme.colors.text60,
    fontSize: '16px',
    '& .highlight': {
      color: theme.colors.text,
    },
  };
});

const NoReceiveMailTitle = styled('div', {
  shouldForwardProp: (props) => isPropValid(props),
})(({ theme }) => {
  return {
    marginBottom: 12,
    color: theme.colors.text,
    fontWeight: 500,
  };
});

export default function MailAuthorize(props) {
  const { onSuccess, trackingConfig = {}, classes = {} } = props;

  const theme = useTheme();

  const { message } = useSnackbar();

  const { t } = useLang();
  const dispatch = useDispatch();

  const { email, riskTag, isShowMailAuthorizePage } = useSelector((state) => state[NAMESPACE]);

  const tracksourceParam = getTrackingSource(trackingConfig);

  useEffect(() => {
    kcsensorsManualTrack(
      {
        checkID: false,
        data: { page_id: 'B1EmailAuthorization' },
      },
      'expose',
    );
    dispatch({
      type: `${NAMESPACE}/triggerPolling`,
    }).then(() => {
      startPolling();
    });
    return () => {
      cancelPolling();
    };
  }, []);

  const startPolling = useCallback(() => {
    dispatch({
      type: `${NAMESPACE}/getMailVerifyResult@polling`,
      payload: {
        onSuccess,
        trackResultParams: { source: tracksourceParam },
      },
    });
  }, [onSuccess, tracksourceParam]);

  const cancelPolling = useCallback(() => {
    dispatch({
      type: `${NAMESPACE}/getMailVerifyResult@polling:cancel`,
    });
  }, []);

  const resent = useMemo(
    () =>
      throttle(
        () => {
          kcsensorsManualTrack(
            {
              kc_pageid: 'B1EmailAuthorization',
              spm: ['B1EmailAuthorization', ['restart', '1']],
            },
            'page_click',
          );
          dispatch({
            type: `${NAMESPACE}/resendMail`,
          })
            .then((res) => {
              if (res?.success) {
                message.success(t('voice_send_success'));
                cancelPolling();
                startPolling();
              }
            })
            .catch((e) => {
              // 授权过期, 返回第一步登录
              if (e?.code === MAIL_AUTHORIZE_EXPIRE_CODE) {
                dispatch({
                  type: `${NAMESPACE}/reset`,
                });
              }
            });
        },
        5000,
        { leading: true },
      ),
    [startPolling, cancelPolling],
  );

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box flex="1">
        <Box as="img" src={emailAuthorizeSvg} alt="" />
        <Title className={classes.title} theme={theme}>
          {t('rXv9y4kZ7AccZiMLqYTBro')}
        </Title>
        {/* <Box className={classes.guide}>确认邮件已发送至您的邮箱 <span className="highlight">dgaergathtr@KuCoin.com</span> 请进入邮箱查看邮件，并完成授权登录</Box> */}
        <Guide className={classes.guide} theme={theme}>
          <Trans i18nKey="ciQtQYEL8RXabtae6Khqnq" ns="entrance" values={{ a: email }}>
            确认邮件已发送至您的邮箱<span className="highlight">email</span>
            。请进入邮箱查看邮件，并完成授权登录
          </Trans>
        </Guide>
        <Tip theme={theme} className={classes.tip}>
          {t('fxAot6UBzQXyeX717mTesc')}
        </Tip>
        <NoReceiveMail className={classes.noReceiveMail}>
          <NoReceiveMailTitle className={classes.noReceiveMailTitle}>
            {t('pN4HQ7gVKL8y3bJhqERfHF')}
          </NoReceiveMailTitle>
          <div>
            <Trans i18nKey="qUDyvur199QMeNMDRT39qb" ns="entrance">
              1.确认您的<span className="highlight">邮箱地址</span>是否正确。
            </Trans>
          </div>
          <div>
            <Trans i18nKey="tDWJZxy1MDHzVhmWsg1mdV" ns="entrance">
              2. 检查您的<span className="highlight">邮件垃圾桶</span>
            </Trans>
          </div>
          <div>
            <Trans i18nKey="3SUHx4KsXKkvMZw1TgBChZ" ns="entrance">
              3. 仍未收到邮件或授权链接已失效，请尝试
              <Box as="a" onClick={!riskTag && isShowMailAuthorizePage ? noop : resent}>
                重新发送
              </Box>
            </Trans>
          </div>
        </NoReceiveMail>
      </Box>
    </Box>
  );
}
